export type DomainCategory = 'Revenue' | 'Finance' | 'Support' | 'Internal' | 'System' | 'Noise';
export type ActionType =
  | 'Requires Decision'
  | 'Requires Deep Work'
  | 'Quick Approval'
  | 'FYI'
  | 'Meeting';
export type DifficultyLevel = 'Easy' | 'Moderate' | 'Hard';
export type ThreadState = 'New' | 'Chaser' | 'Waiting on Them' | 'Imminent Deadline' | 'Stalled';

/**
 * Where a thread is in the *analysis* lifecycle, which is independent of whether
 * the thread was ingested. Ingest always writes a row; analysis decorates it and
 * is allowed to fail, be deferred, be declined, or never run. See ADR-0003.
 *
 * Note there is no `skipped` here on purpose: the ingest layer already uses that
 * word for "unchanged, no re-summarization needed", which is a different idea.
 */
export type AnalysisStatus = 'pending' | 'analyzed' | 'failed' | 'excluded';

/** Why a thread has no analysis. Null/absent when `analysisStatus` is `analyzed`. */
export type AnalysisReason =
  | 'provider_error'
  | 'quota_exhausted'
  | 'content_unsupported'
  | 'disabled'
  | 'before_start_date';

/**
 * The reasons the retry sweep re-attempts. The two config reasons (`disabled`,
 * `before_start_date`) are deliberately absent — sweeping them would rebuild the
 * cost problem those settings exist to avoid — and `content_unsupported` will
 * fail identically every time.
 */
export const RETRYABLE_ANALYSIS_REASONS = [
  'provider_error',
  'quota_exhausted',
] as const satisfies readonly AnalysisReason[];

export function isRetryableAnalysisReason(reason: string | null | undefined): boolean {
  return (RETRYABLE_ANALYSIS_REASONS as readonly string[]).includes(reason ?? '');
}

/** User-facing copy for why a thread has no summary. Keep short — it renders inline on a row. */
export const ANALYSIS_REASON_LABELS: Record<AnalysisReason, string> = {
  provider_error: 'Analysis failed',
  quota_exhausted: 'Usage limit reached',
  content_unsupported: 'Could not be analyzed',
  disabled: 'AI analysis is off',
  before_start_date: 'Predates AI analysis',
};

export interface EmailThreadTask {
  id: number;
  label: string;
  status: string | null;
  dueAt: number | null;
  priority: number | null;
  assignedTo?: number | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
}

export interface LabelCategoryMapping {
  labelId: string;
  categoryId: number;
}

export interface TriageCategory {
  id: number;
  name: string;
  color: string;
  weight: number;
}

export interface EmailThreadMetadata {
  threadId: string; // Original Gmail Thread ID
  workspaceId?: number;
  /**
   * The raw `Subject:` header. Always present on threads ingested since ADR-0003 —
   * this is the display fallback when there is no analysis, and the reason a row can
   * exist without one. Prefer `threadDisplayTitle()` over reading it directly.
   */
  subject?: string | null;
  /**
   * The seven fields below are AI-derived and null until analysis succeeds. Check
   * `analysisStatus` to tell "not analyzed yet" from "analyzed to nothing", and use
   * the helpers in `shared/src/lib/clinic-logic.ts` rather than defaulting inline —
   * an unanalysed thread needs a defined position in every filter, sort and group,
   * not just a rendering fallback.
   */
  synthesizedTitle: string | null; // AI-generated dynamic title
  domainCategory: DomainCategory | null;
  actionType: ActionType | null;
  difficultyLevel: DifficultyLevel | null;
  estimatedEffortMinutes: number | null;
  threadState: ThreadState | null;
  extractedEntities: {
    monetaryValues: string[];
    keyStakeholders: string[];
    deadlines: string[];
  };
  coreAsk: string | null;
  /**
   * Provider-independent, model-generated classification tokens. Null means this
   * thread has no tag analysis; an analyzed thread with no tags carries `[]`.
   * Distinct from provider labels in `labels`.
   */
  analysisTags: string[] | null;
  analysisStatus?: AnalysisStatus;
  analysisReason?: AnalysisReason | null;
  analyzedAt?: number | null;
  draftResponse?: string;
  accountEmail?: string;
  /**
   * Provider deep link, when the provider issues one rather than letting us build
   * it (Outlook's webLink, captured at ingest). Absent for Gmail, whose URL is
   * constructed — see mailThreadLink, which is the only thing that should read
   * this (its presence also signals which provider the thread belongs to).
   */
  webUrl?: string | null;
  listUnsubscribe?: string | null;
  senderEmail?: string;
  senderName?: string;
  contact?: { id: number; label: string; typeId: number } | null;
  contactId?: number | null;
  labels: string[];
  createdAt?: number;
  task?: EmailThreadTask | null;
  firstResponseAt?: number | null;
  resolvedAt?: number | null;
  resolvedBy?: number | null;
  /**
   * The conversation this thread belongs to. Absent on surfaces that do not join it, and
   * null only between ingest writing the row and giving it a conversation. Links use it,
   * never `threadId` — format it with `threadLinkRef()`.
   */
  conversationId?: number | null;
}

/**
 * A single email message within a thread, as returned by
 * `GET /api/conversations/:conversationId/messages`.
 *
 * Lives here rather than beside its renderer (`ThreadMessageList`) so non-web
 * consumers — the React Native app — can type the API response without pulling a
 * DOM component (and its Vite-only `import.meta.env` dependencies) into the bundle.
 */
export interface ThreadMessage {
  id: string;
  from: { name: string; email: string };
  to: string;
  subject: string;
  timestampMs: number;
  bodyText: string;
  /**
   * Retained, already-sanitized HTML — present only for a workspace enrolled in content
   * retention whose copy of this message has not been purged. Optional because the plain
   * text is what every workspace has, and "no HTML" is a normal state rather than a
   * degraded one.
   *
   * Sanitized again at render by `MessageBody`. Never pass it to
   * `dangerouslySetInnerHTML` directly.
   */
  bodyHtml?: string | null;
  labelIds: string[];
  /**
   * The stable internal id of the retained copy, when this row came from retained
   * evidence rather than a live provider fetch. Present only on a retained timeline —
   * a provider fetch has no internal ids to hand.
   *
   * What `clinicContent.getMessageContent` takes. The body is deliberately absent from
   * the list response: it is read one message at a time, with its own capability check
   * and audit event.
   */
  clinicMessageId?: number | null;
  /** True when this row is retained evidence, so its body has to be asked for. */
  retained?: boolean;
  /** False once retention purged the body. The message still anchors a citation. */
  hasStoredContent?: boolean;
}

/** A task linked to a mail conversation, from `trpc.tasks.getByConversation`. */
export interface ThreadTask {
  id: number;
  label: string;
  content: string | null;
  status: string;
  priority: number;
  dueAt: number | null;
  assignedTo: number | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  createdAt: number;
}

export interface AnchorTask {
  taskId: string;
  assignedToUserId: string;
  assignedByUserId: string;
  status: 'pending' | 'resolved';
  taskDescription: string;
  resolutionNote?: string;
  createdAt: string;
}
